import * as amplitude from '@amplitude/analytics-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import { orderTypeIds } from '../constants/order';
import { storeIds } from '../constants/store';
import { buildAttributionParametersJson } from './amplitudeAttribution';
import ddLog from './datadog/log';
import {
  countLineItemProductOptions,
  findAddedOrderLine,
  findLatestOrderLineForProduct,
  formatOrderSubtotalForAmplitude,
  getAmplitudeProductType,
  getProductRollupCategory,
  resolveAddFirstItemToCartEventPropertiesFromOrder,
  resolveAddToOrderPageName,
  resolveCompleteOrderEventProperties,
  resolvePlaceOrderEventProperties,
  resolveViewCheckoutOrderBaseProperties,
  sumOrderLineItemQuantities,
} from './amplitudeCommerce';
import {
  getAmplitudeAddressSet,
  resolveAmplitudePageName,
  resolvePageNameForNavEvent,
  shouldIncludeAmplitudeIsLoggedIn,
} from './amplitudePageMapping';

export const AMPLITUDE_NAV_SECTION = {
  TOP_NAV: 'Top Nav',
  HAMBURGER_MENU: 'Hamburger Menu',
  FOOTER: 'Footer',
};

export const AMPLITUDE_NAV_SECTION_SECONDARY = {
  CATEGORIES: 'Categories',
  ORDER_AGAIN: 'Order again',
  PRODUCT_PAGE: 'Product',
  CHECKOUT: 'Checkout',
  ORDER_SUMMARY: 'Order Summary',
};

const BROWSER_AMPLITUDE_PLACEHOLDER_KEY = 'browser-ingest-proxy';

export const AMPLITUDE_PROXY_PATH = '/client-ingest/amplitude/2/httpapi';

const AUTOCAPTURE_OPTIONS = {
  attribution: true,
  pageViews: {
    trackHistoryChanges: 'pathOnly',
  },
  sessions: true,
  formInteractions: true,
  fileDownloads: true,
  elementInteractions: false,
};

let amplitudeClientReady = false;
let amplitudeReadyPromise = Promise.resolve();

/** @type {boolean} */
let sessionReplayPluginRegistered = false;

/**
 * Test-only: reset module state so Jest can exercise Session Replay registration in isolation.
 */
export const resetSessionReplayPluginStateForTests = () => {
  sessionReplayPluginRegistered = false;
};

export const isAmplitudeSessionReplayEnabled = (environment) =>
  String(environment?.REACT_APP_AMPLITUDE_SESSION_REPLAY_ENABLED || '').toLowerCase() === 'true';

const parseSessionReplaySampleRate = (environment) => {
  const raw = String(environment?.REACT_APP_AMPLITUDE_SESSION_REPLAY_SAMPLE_RATE ?? '').trim();
  if (raw === '') {
    return 0.01;
  }
  const n = Number.parseFloat(raw);
  if (!Number.isFinite(n) || n < 0 || n > 1) {
    return 0.01;
  }
  return n;
};

const resolveSessionReplayServerZone = (environment) => {
  const z = String(environment?.REACT_APP_AMPLITUDE_SESSION_REPLAY_SERVER_ZONE || 'US').trim().toUpperCase();
  return z === 'EU' ? 'EU' : 'US';
};

/**
 * Registers Session Replay after `amplitude.init` (see @amplitude/plugin-session-replay-browser README).
 * Safe no-op when disabled or when registration already ran.
 */
const maybeRegisterSessionReplayPlugin = (environment) => {
  if (sessionReplayPluginRegistered || !isAmplitudeSessionReplayEnabled(environment)) {
    return;
  }
  try {
    const plugin = sessionReplayPlugin({
      sampleRate: parseSessionReplaySampleRate(environment),
      serverZone: resolveSessionReplayServerZone(environment),
    });
    amplitude.add(plugin);
    sessionReplayPluginRegistered = true;
  } catch (err) {
    ddLog.logError(err instanceof Error ? err : new Error(String(err)));
  }
};

export const isAmplitudeEnabled = (environment) => {
  const val = environment?.REACT_APP_AMPLITUDE_ENABLED;
  return val !== false && val !== 'false' && val !== '0';
};

export const isAmplitudeProxyEnabled = (environment) =>
  String(environment?.REACT_APP_AMPLITUDE_USE_PROXY || '').toLowerCase() === 'true';

export const isUnsubstitutedEnvToken = (value) =>
  /^#\{[^}]+\}#$/.test(String(value ?? '').trim());

export const resolveAmplitudeServerUrl = (environment) => {
  const gql = environment?.REACT_APP_GRAPHQL_URI;
  if (gql == null || String(gql).trim() === '') {
    return undefined;
  }
  const base = String(gql).replace(/\/?graphql\/?$/i, '').replace(/\/$/, '');
  return `${base}${AMPLITUDE_PROXY_PATH}`;
};

const syncAttributionUserProperties = () => {
  if (!amplitudeClientReady || typeof window === 'undefined') {
    return;
  }
  try {
    const json = buildAttributionParametersJson(window.location.search, document.referrer);
    if (!json) {
      return;
    }
    const identifyEvent = new amplitude.Identify();
    identifyEvent.set('Attribution Parameters', json);
    amplitude.identify(identifyEvent);
  } catch {
    /* Identify / SDK must not break app load or navigation */
  }
};

const initialize = (environment) => {
  if (!isAmplitudeEnabled(environment)) {
    return;
  }
  const baseOptions = {
    fetchRemoteConfig: false,
    autocapture: AUTOCAPTURE_OPTIONS,
  };

  if (isAmplitudeProxyEnabled(environment)) {
    const serverUrl = resolveAmplitudeServerUrl(environment);
    if (!serverUrl) {
      return;
    }
    amplitudeReadyPromise = amplitude.init(BROWSER_AMPLITUDE_PLACEHOLDER_KEY, {
      ...baseOptions,
      serverUrl,
    }).promise ?? Promise.resolve();
    maybeRegisterSessionReplayPlugin(environment);
    amplitudeClientReady = true;
    syncAttributionUserProperties();
    return;
  }

  const apiKey = String(environment?.REACT_APP_AMPLITUDE_API_KEY || '').trim();
  if (!apiKey || isUnsubstitutedEnvToken(apiKey)) {
    return;
  }

  const directOptions = { ...baseOptions };
  const customIngest = String(environment?.REACT_APP_AMPLITUDE_SERVER_URL || '').trim();
  if (customIngest && !isUnsubstitutedEnvToken(customIngest)) {
    directOptions.serverUrl = customIngest;
  }

  amplitudeReadyPromise = amplitude.init(apiKey, directOptions).promise ?? Promise.resolve();
  maybeRegisterSessionReplayPlugin(environment);
  amplitudeClientReady = true;
  syncAttributionUserProperties();
};

const getReadyPromise = () => amplitudeReadyPromise;

const syncUserId = (userId) => {
  if (!amplitudeClientReady) {
    return;
  }
  const hasId = userId != null && String(userId).trim() !== '';
  amplitude.setUserId(hasId ? String(userId) : undefined);
};

const trackViewPage = ({ pageName, userSlice } = {}) => {
  if (!amplitudeClientReady || !pageName) {
    return;
  }
  syncAttributionUserProperties();
  const addressSet = getAmplitudeAddressSet(userSlice);
  const payload = {
    'Page Name': pageName,
    'Address Set': addressSet,
  };
  if (shouldIncludeAmplitudeIsLoggedIn(pageName)) {
    const token = userSlice?.userToken;
    payload['Is Logged In'] = token != null && String(token).trim() !== '';
  }
  amplitude.track('View Page', payload);
};

/**
 * Amplitude — Clicked Promotion Element.
 * Trigger: click on Braze content cards (hero and non-hero templates).
 * @param {{ pageName: string, cardSort?: string|number }} params
 */
const trackClickedPromotionElement = ({ pageName, cardSort } = {}) => {
  if (!amplitudeClientReady || !pageName) {
    return;
  }
  try {
    amplitude.track('Clicked Promotion Element', {
      'Page Name': pageName,
      'Card Sort': cardSort != null && String(cardSort).trim() !== '' ? String(cardSort) : '',
    });
  } catch {
    /* analytics must not affect hero click flow */
  }
};

/**
 * Amplitude — Click Page Content.
 * Trigger: click on non-navigation page content elements (including content cards).
 * @param {{ pageName: string, contentSection: string, contentElement?: string }} params
 */
const trackClickPageContent = ({ pageName, contentSection, contentElement } = {}) => {
  if (!amplitudeClientReady || !pageName || !contentSection) {
    return;
  }
  const element = contentElement != null ? String(contentElement).trim() : '';
  const eventProps = {
    'Page Name': pageName,
    'Content Section': contentSection,
  };
  if (element) {
    eventProps['Content Element'] = element;
  }
  try {
    amplitude.track('Click Page Content', eventProps);
  } catch {
    /* analytics must not affect page-content click flow */
  }
};

/**
 * Click Page Content for CMS CTAs when pathname maps to a ticket page (e.g. Home, Gifting LP).
 * No-op when pathname is not mapped (avoids noise from country-picker-only routes, etc.).
 */
const trackCmsMappedPageClick = ({ pathname, contentSection, contentElement } = {}) => {
  const pageName = resolveAmplitudePageName(pathname);
  if (!pageName) {
    return;
  }
  const section = contentSection != null ? String(contentSection).trim() : '';
  const element = contentElement != null ? String(contentElement).trim() : '';
  trackClickPageContent({
    pageName,
    contentSection: section || 'CMS',
    contentElement: element || 'CTA',
  });
};

/**
 * Amplitude: "Filter PDP" — tag filter on packs/box PDP to narrow PRIMARY cookie list.
 * @param {{ productName?: string, productId?: string|number, filterName?: string }} params
 */
const trackFilterPdp = ({
  productName,
  productId,
  filterName,
} = {}) => {
  if (!amplitudeClientReady || filterName == null || String(filterName).trim() === '') {
    return;
  }
  try {
    amplitude.track('Filter PDP', {
      'Product Name': productName != null ? String(productName) : '',
      'Product ID': productId != null ? String(productId) : '',
      'Filter Name': String(filterName),
    });
  } catch {
    /* analytics must not break PDP */
  }
};

/**
 * Amplitude: "View Gift Options" — gift sleeve selection page viewed (after gift PDP add).
 * @param {{ productName?: string, productId?: string|number }} params
 */
const trackViewGiftOptions = ({ productName, productId } = {}) => {
  if (!amplitudeClientReady || productId == null || String(productId).trim() === '') {
    return;
  }
  try {
    amplitude.track('View Gift Options', {
      'Product Name': productName != null ? String(productName) : '',
      'Product ID': String(productId),
    });
  } catch {
    /* analytics must not break gift flow */
  }
};

/**
 * Amplitude: "Select Gift Option" — user confirms a sleeve or chooses no gift box.
 * @param {{ productName?: string, productId?: string|number, optionName?: string }} params
 */
const trackSelectGiftOption = ({
  productName,
  productId,
  optionName,
} = {}) => {
  if (!amplitudeClientReady || productId == null || String(productId).trim() === '') {
    return;
  }
  if (optionName == null || String(optionName).trim() === '') {
    return;
  }
  try {
    amplitude.track('Select Gift Option', {
      'Product Name': productName != null ? String(productName) : '',
      'Product ID': String(productId),
      'Option Name': String(optionName),
    });
  } catch {
    /* analytics must not break gift flow */
  }
};

export const formatAmplitudeSuggestedItemCategory = (categories) => {
  if (!Array.isArray(categories) || categories.length === 0) {
    return '';
  }
  const seen = new Set();
  const labels = [];
  categories.forEach((c) => {
    if (!c || typeof c !== 'object') {
      return;
    }
    const raw = c.title || c.name || c.slug || '';
    const label = String(raw).trim();
    if (!label) {
      return;
    }
    const key = label.toLowerCase();
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    labels.push(label);
  });
  return labels.join(', ');
};

/**
 * Page context when adding from the suggested-items carousel on Review Order or View Order.
 * @param {string} [pathname]
 * @returns {'Checkout'|'View Order'|null}
 */
export const getAmplitudeSuggestedItemPageNameForProductCard = (pathname) => {
  const p = String(pathname || '');
  if (/\/checkout\/review-order(\/|$)/.test(p)) {
    return 'Checkout';
  }
  if (/\/checkout\/view-order(\/|$)/.test(p)) {
    return 'View Order';
  }
  return null;
};

/**
 * Cart complementary items: user lands on PDP with `isUpsale=true` from the cart dropdown.
 * After ProductRedirect, path is slug-based (/products/:slug) but query must still contain isUpsale.
 * @param {string} [pathname]
 * @param {string} [search] - `location.search` (may include leading `?`)
 * @returns {'Cart'|null}
 */
export const getAmplitudeSuggestedItemPageNameForPdp = (pathname, search) => {
  const p = String(pathname || '');
  const s = String(search || '');
  let isUpsale = false;
  try {
    const qs = s.startsWith('?') ? s.slice(1) : s;
    isUpsale = new URLSearchParams(qs).get('isUpsale') === 'true';
  } catch {
    return null;
  }
  if (!isUpsale) {
    return null;
  }
  if (!p.includes('/products/')) {
    return null;
  }
  if (p.includes('/products/custom-cake-topper/')) {
    return null;
  }
  if (/\/products\/[^/]+\/[^/]+\/options(\/|$)/.test(p)) {
    return null;
  }
  return 'Cart';
};

/**
 * Amplitude: "Add Suggested Item" — direct add from Cart / Checkout / View Order suggested flow.
 * @param {{ pageName: string, productCategory?: string, productName?: string, productId?: string|number }} params
 */
const trackAddSuggestedItem = ({
  pageName,
  productCategory,
  productName,
  productId,
} = {}) => {
  if (!amplitudeClientReady || !pageName) {
    return;
  }
  try {
    amplitude.track('Add Suggested Item', {
      'Page Name': pageName,
      'Product Category': productCategory != null ? String(productCategory) : '',
      'Product Name': productName != null ? String(productName) : '',
      'Product ID': productId != null ? String(productId) : '',
    });
  } catch {
    /* analytics must not break cart or checkout */
  }
};

/**
 * Single-line address for Amplitude (Enter Address). #18398
 * @param {object|string|null|undefined} addr
 */
const formatAmplitudeDeliveryAddressLine = (addr) => {
  if (addr == null) {
    return '';
  }
  if (typeof addr === 'string') {
    return addr.trim();
  }
  const line1 = [addr.address1, addr.address2].filter(Boolean).join(', ');
  const locality = [addr.city, addr.state, addr.postcode].filter(Boolean).join(', ');
  const country = addr.country?.name || addr.countryName || '';
  return [line1, locality, country].filter(Boolean).join(', ');
};

/**
 * Search Menu event.
 * @param {{ queryString?: string }} params
 */
const trackSearchMenu = ({ queryString } = {}) => {
  if (!amplitudeClientReady) {
    return;
  }
  const normalizedQuery = String(queryString || '').trim();
  if (!normalizedQuery) {
    return;
  }
  amplitude.track('Search Menu', {
    'Query String': normalizedQuery,
  });
};

/**
 * Click Product Card event.
 * @param {{
 *  pageName?: string,
 *  productCategory?: string,
 *  productName?: string,
 *  productId?: string|number,
 *  productType?: string
 * }} params
 */
const trackClickProductCard = ({
  pageName,
  productCategory,
  productName,
  productId,
  productType,
} = {}) => {
  if (!amplitudeClientReady || !pageName || !productName || productId == null) {
    return;
  }
  amplitude.track('Click Product Card', {
    'Page Name': pageName,
    'Product Category': productCategory || '',
    'Product Name': productName,
    'Product ID': productId,
    'Product Type': productType || '',
  });
};

/**
 * Checkout: promo / gift card Apply clicked (#18413).
 * @param {{ isAccepted: boolean, coupon?: string, couponSavings?: string, giftCardNumbers?: string, giftCardSavings?: string }} params
 */
const trackEnterPromoCodeGiftCard = ({
  isAccepted,
  coupon = '',
  couponSavings = '',
  giftCardNumbers = '',
  giftCardSavings = '',
} = {}) => {
  if (!amplitudeClientReady) {
    return;
  }
  try {
    amplitude.track('Enter Promo Code/Gift Card', {
      'Is Accepted': Boolean(isAccepted),
      Coupon: String(coupon),
      'Coupon Savings': String(couponSavings),
      'Gift Card Number(s)': String(giftCardNumbers),
      'Gift Card Savings': String(giftCardSavings),
    });
  } catch {
    /* Must not break checkout / payment if Amplitude or payload handling throws */
  }
};

/**
 * Checkout: dropoff pin saved successfully (#18413).
 * @param {{ latLong: string }} params
 */
const trackSetDropoffLocation = ({ latLong } = {}) => {
  if (!amplitudeClientReady || !latLong) {
    return;
  }
  try {
    amplitude.track('Set Dropoff Location', {
      'Lat / Long': String(latLong),
    });
  } catch {
    /* Must not break checkout if Amplitude throws */
  }
};

/**
 * Checkout: credit/debit Next / Add card submitted (#18413).
 * @param {{ isAccepted: boolean }} params
 */
const trackAddCreditCard = ({ isAccepted } = {}) => {
  if (!amplitudeClientReady) {
    return;
  }
  if (isAccepted !== true && isAccepted !== false) {
    return;
  }
  try {
    amplitude.track('Add Credit Card', {
      'Is Accepted': Boolean(isAccepted),
    });
  } catch {
    /* Must not break payment / card submission if Amplitude throws */
  }
};

/**
 * Delivery/pickup widget viewed (#18398). Callers may pass explicit `pageName` or `pathname` (resolved to Page Name).
 */
const trackViewDeliveryPickupWidget = ({
  fulfillmentType,
  pageName: pageNameParam,
  pathname,
  pageSection,
  addressChange,
} = {}) => {
  if (!amplitudeClientReady || !fulfillmentType || !pageSection) {
    return;
  }
  const pageName =
    pageNameParam != null && String(pageNameParam).trim() !== ''
      ? String(pageNameParam).trim()
      : resolvePageNameForNavEvent(pathname || '') || '';
  if (!pageName) {
    return;
  }
  const payload = {
    'Fulfillment Type': fulfillmentType,
    'Page Name': pageName,
    'Page Section': pageSection,
  };
  if (typeof addressChange === 'boolean') {
    payload['Address Change'] = addressChange;
  }
  amplitude.track('View Delivery/Pickup Widget', payload);
};

/**
 * Address confirmed in delivery/pickup flows (#18398). Accepts explicit `pageName` or `pathname`; formats `Address` line.
 */
const trackEnterAddress = ({
  pageName: pageNameParam,
  pathname,
  fulfillmentType,
  addressSource,
  address,
} = {}) => {
  if (!amplitudeClientReady || !fulfillmentType || !addressSource) {
    return;
  }
  const pageName =
    pageNameParam != null && String(pageNameParam).trim() !== ''
      ? String(pageNameParam).trim()
      : resolvePageNameForNavEvent(pathname || '');
  if (!pageName) {
    return;
  }
  const line = formatAmplitudeDeliveryAddressLine(address);
  if (!line) {
    return;
  }
  amplitude.track('Enter Address', {
    'Page Name': pageName,
    'Fulfillment Type': fulfillmentType,
    'Address Source': addressSource,
    Address: line,
  });
};

/**
 * View Order: user taps Modify Order to edit a line item on PDP.
 * @param {{ productName?: string, productId?: string|number }} params
 */
const trackClickToModifyItem = ({ productName, productId } = {}) => {
  if (!amplitudeClientReady || !productName || productId == null) {
    return;
  }
  amplitude.track('Click to Modify Item', {
    'Product Name': productName,
    'Product ID': productId,
  });
};

/**
 * PDP: user submits Update Order after editing a cart line.
 * @param {{ productName?: string, productId?: string|number }} params
 */
const trackUpdateItem = ({ productName, productId } = {}) => {
  if (!amplitudeClientReady || !productName || productId == null) {
    return;
  }
  amplitude.track('Update Item', {
    'Product Name': productName,
    'Product ID': productId,
  });
};

/**
 * Remove line item from cart (View Order, Order Summary, or header cart).
 * @param {{ productName?: string, productId?: string|number, pageName?: string }} params
 */
const trackRemoveItem = ({ productName, productId, pageName } = {}) => {
  if (!amplitudeClientReady || !productName || productId == null || !pageName) {
    return;
  }
  amplitude.track('Remove Item', {
    'Product Name': productName,
    'Product ID': productId,
    'Page Name': pageName,
  });
};

/**
 * Cart quantity stepper (+/-).
 * @param {{ productName?: string, productId?: string|number, pageName?: string, increaseOrDecrease?: string }} params
 */
const trackModifyItemQuantity = ({
  productName,
  productId,
  pageName,
  increaseOrDecrease,
} = {}) => {
  if (!amplitudeClientReady || !productName || productId == null || !pageName || !increaseOrDecrease) {
    return;
  }
  amplitude.track('Modify Item Quantity', {
    'Product Name': productName,
    'Product ID': productId,
    'Page Name': pageName,
    'Increase or Decrease': increaseOrDecrease,
  });
};

/**
 * Sign In (Amplitude custom event).
 * Email: after password login API resolves.
 * Phone: after 4-digit OTP is submitted and `confirmCustomerTelephoneOtp` resolves — not on phone-number submit.
 * `Is Accepted` reflects that API outcome (wrong OTP / errors => false). No event if the user never completes OTP.
 * User property `User ID` is set via identify when `customerId` is present (successful sign-in).
 * @param {{ signInMethod: 'Phone'|'Email', isAccepted: boolean, customerId?: string|number, isPasswordless?: boolean }} params
 */
const trackSignIn = ({ signInMethod, isAccepted, customerId, isPasswordless = false } = {}) => {
  if (!amplitudeClientReady || !signInMethod) {
    return;
  }
  try {
    if (customerId != null && String(customerId).trim() !== '') {
      const identifyEvent = new amplitude.Identify();
      identifyEvent.set('User ID', String(customerId).trim());
      amplitude.identify(identifyEvent);
    }
    amplitude.track('Sign In', {
      'Sign In Method': signInMethod,
      'Is Accepted': Boolean(isAccepted),
      'Is Passwordless': signInMethod === 'Email' && Boolean(isPasswordless),
    });
  } catch {
    /* analytics must not affect auth */
  }
};

/**
 * Amplitude — View Checkout (`/checkout/review-order/:id`).
 * @param {object} order — loaded order with line items
 * @param {object} [reduxState] — `store.getState()`
 */
const trackViewCheckout = (order, reduxState) => {
  if (!amplitudeClientReady || !order?.code) {
    return;
  }
  try {
    if (!Array.isArray(order.items) || order.items.length === 0) {
      return;
    }
    const payload = resolveViewCheckoutOrderBaseProperties(order, reduxState);
    amplitude.track('View Checkout', payload);
  } catch (err) {
    try {
      const message = err instanceof Error ? err.message : String(err ?? 'unknown');
      ddLog.logError(new Error(`Amplitude trackViewCheckout failed: ${message}`));
    } catch {
      /* logging must not affect checkout */
    }
  }
};

/**
 * Create Account after signup API resolves.
 * `Is Accepted` reflects whether the account was created successfully (API success), not form validation alone.
 * @param {{ isAccepted: boolean, referralCodeIncluded: boolean, promoEmailOptIn: boolean }} params
 */
const trackCreateAccount = ({
  isAccepted,
  referralCodeIncluded,
  promoEmailOptIn,
} = {}) => {
  if (!amplitudeClientReady) {
    return;
  }
  try {
    amplitude.track('Create Account', {
      'Is Accepted': Boolean(isAccepted),
      'Referral Code Included': Boolean(referralCodeIncluded),
      'Promo Email Opt-in': Boolean(promoEmailOptIn),
    });
  } catch {
    /* analytics must not affect signup */
  }
};

/**
 * Reset Password (forgot-password submit). No event properties (PM confirmed).
 */
const trackResetPassword = () => {
  if (!amplitudeClientReady) {
    return;
  }
  try {
    amplitude.track('Reset Password');
  } catch {
    /* analytics must not affect reset flow */
  }
};

/**
 * Amplitude #18380 — persistent header/footer/hamburger nav clicks.
 * @param {{ navElement: string, navSection: string, pathname?: string, countryPathUrl?: string }} params
 */
const trackClickNavPrimary = ({ navElement, navSection, pathname, countryPathUrl } = {}) => {
  if (!amplitudeClientReady || !navElement || !navSection) {
    return;
  }
  const pageName = resolvePageNameForNavEvent(pathname, countryPathUrl);
  const eventProps = {
    'Nav Element': navElement,
    'Nav Section': navSection,
  };
  if (pageName) {
    eventProps['Page Name'] = pageName;
  }
  amplitude.track('Click Nav - Primary', eventProps);
};

const trackOpenHamburgerMenu = ({ pathname, countryPathUrl } = {}) => {
  if (!amplitudeClientReady) {
    return;
  }
  const pageName = resolvePageNameForNavEvent(pathname, countryPathUrl);
  const eventProps = {};
  if (pageName) {
    eventProps['Page Name'] = pageName;
  }
  amplitude.track('Open Hamburger Menu', eventProps);
};

/**
 * #18380 PM: omit "Nav Section" on Click Nav - Secondary from Checkout and PDP (testing sheet).
 * Uses the same Page Name mapping as nav events so country-prefixed paths match.
 * @param {string|undefined} pageName - from {@link resolvePageNameForNavEvent}
 * @returns {boolean}
 */
const shouldOmitNavSectionOnClickNavSecondary = (pageName) =>
  pageName === 'Checkout' || pageName === 'Order Summary' || pageName === 'PDP';

const trackClickNavSecondary = ({ navElement, navSection, pathname, countryPathUrl } = {}) => {
  if (!amplitudeClientReady || !navElement) {
    return;
  }
  const pageName = resolvePageNameForNavEvent(pathname, countryPathUrl);
  const omitNavSection = shouldOmitNavSectionOnClickNavSecondary(pageName);
  if (
    !omitNavSection
    && (navSection == null || String(navSection).trim() === '')
  ) {
    return;
  }
  const eventProps = {
    'Nav Element': navElement,
  };
  if (!omitNavSection) {
    eventProps['Nav Section'] = navSection;
  }
  if (pageName) {
    eventProps['Page Name'] = pageName;
  }
  amplitude.track('Click Nav - Secondary', eventProps);
};

const formatAddressLineForAmplitude = (addr) => {
  if (!addr || typeof addr !== 'object') {
    return '';
  }
  const line1 = [addr.address1, addr.address2].filter(Boolean).join(', ');
  const cityState = [addr.city, addr.state].filter(Boolean).join(', ');
  const country = addr.country != null && String(addr.country).trim() !== ''
    ? String(addr.country).trim()
    : '';
  const postcode = addr.postcode != null && String(addr.postcode).trim() !== ''
    ? String(addr.postcode).trim()
    : '';
  const segments = [line1, cityState, country, postcode].filter(
    (s) => s != null && String(s).trim() !== '',
  );
  return segments.map((s) => String(s).trim()).join(', ');
};

const formatOrderCreatedDateForAmplitude = (date = new Date()) => {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) {
    return '';
  }
  const timeZone = 'America/New_York';
  const options = {
    timeZone,
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  };
  let formatted = new Intl.DateTimeFormat('en-US', options).format(d);
  const subMs = ((d.getTime() % 1000) + 1000) % 1000;
  formatted = formatted.replace(
    /(\d{1,2}:\d{2}:\d{2}) (AM|PM)/i,
    (_, clock, ap) => `${clock}.${String(subMs).padStart(3, '0')} ${ap}`,
  );
  formatted = formatted.replace(/(\d{4}),/, '$1');
  return formatted;
};

/**
 * Labels must match {@link orderTypeIds} (delivery=2, pickup=3, shipping=6).
 * Legacy code used 1/2/3 and store 15 — that inverted Delivery vs Pickup and Pickup vs Shipping in Amplitude.
 */
const getFulfillmentTypeLabel = ({ orderTypeId, storeId } = {}) => {
  const orderType = Number(orderTypeId);
  if (orderType === orderTypeIds.delivery) {
    return 'Delivery';
  }
  if (orderType === orderTypeIds.pickup) {
    return 'Pickup';
  }
  if (orderType === orderTypeIds.shipping) {
    return 'Shipping';
  }
  if (String(storeId) === String(storeIds.shipping)) {
    return 'Shipping';
  }
  return '';
};

const resolveStartOrderStoreName = (storeIdStr, reduxState) => {
  if (storeIdStr === '') {
    return '';
  }
  if (String(storeIdStr) === String(storeIds.shipping)) {
    return 'Gifts';
  }
  const orderStore = reduxState?.order?.store;
  if (
    orderStore != null &&
    String(orderStore.id) === storeIdStr &&
    orderStore.name != null &&
    String(orderStore.name).trim() !== ''
  ) {
    return String(orderStore.name).trim();
  }
  const selectedStore = reduxState?.user?.selectedStore;
  if (
    selectedStore != null &&
    String(selectedStore.id) === storeIdStr &&
    selectedStore.name != null &&
    String(selectedStore.name).trim() !== ''
  ) {
    return String(selectedStore.name).trim();
  }
  return '';
};

const resolveStartOrderProperties = (variables, reduxState, eventTime = new Date()) => {
  const selectedStore = reduxState?.user?.selectedStore;
  const userAddress = reduxState?.user?.userAddress;
  const orderSlice = reduxState?.order;
  const sid =
    variables?.storeId ??
    orderSlice?.storeId ??
    orderSlice?.store?.id;
  const storeId = sid != null ? String(sid) : '';
  const effectiveOrderTypeId =
    variables?.orderTypeId != null ? variables.orderTypeId : orderSlice?.orderTypeId;
  const fulfillmentType = getFulfillmentTypeLabel({
    orderTypeId: effectiveOrderTypeId,
    storeId: sid,
  });

  let addressLine = formatAddressLineForAmplitude(variables?.address);
  if (!addressLine && selectedStore != null && String(selectedStore.id) === String(sid)) {
    addressLine = formatAddressLineForAmplitude({
      address1: selectedStore.mailingAddress ?? selectedStore.address,
      address2: selectedStore.address2,
      city: selectedStore.city,
      state: selectedStore.state,
      postcode: selectedStore.zip ?? selectedStore.postcode,
    });
  }
  if (!addressLine && userAddress) {
    addressLine = formatAddressLineForAmplitude(userAddress);
  }

  const isShipping = fulfillmentType === 'Shipping';
  const storeName = isShipping ? '' : resolveStartOrderStoreName(storeId, reduxState);

  const payload = {
    'Order Created Date': formatOrderCreatedDateForAmplitude(eventTime),
    'Fulfillment Type': fulfillmentType,
    Address: addressLine,
  };

  /* Shipping: do not send virtual store id/name (PM + analytics spec). */
  if (!isShipping) {
    const idTrimmed = storeId != null ? String(storeId).trim() : '';
    if (idTrimmed !== '') {
      payload['Store ID'] = idTrimmed;
    }
    const nameTrimmed = storeName != null ? String(storeName).trim() : '';
    if (nameTrimmed !== '') {
      payload['Store Name'] = nameTrimmed;
    }
  }

  return payload;
};

/**
 * Start Order — omits Store ID / Store Name for Shipping; for other types, includes those
 * properties only when non-blank (avoid placeholder-only values).
 */
const trackStartOrder = (variables, reduxState, eventTime) => {
  if (!amplitudeClientReady || !variables) {
    return;
  }
  try {
    const when = eventTime === undefined ? new Date() : eventTime;
    const payload = resolveStartOrderProperties(variables, reduxState, when);
    if (!payload.Address || String(payload.Address).trim() === '') {
      return;
    }
    amplitude.track('Start Order', payload);
  } catch {
    /* analytics must not affect checkout or cart */
  }
};

/**
 * Sets spreadsheet user property **User ID** (internal customer id) when known.
 * @param {object} order
 * @param {object} [reduxState]
 */
const identifyInternalUserIdForViewOrder = (order, reduxState) => {
  const id = order?.customer?.id ?? reduxState?.user?.userId;
  if (id == null || String(id).trim() === '') {
    return;
  }
  const identifyEvent = new amplitude.Identify();
  identifyEvent.set('User ID', String(id));
  amplitude.identify(identifyEvent);
};

/**
 * Amplitude — View Order (`/checkout/view-order`).
 * @param {object} order — order with line items
 * @param {object} [reduxState] — `store.getState()`
 * @param {string} [currencySymbol] — for Subtotal display
 */
const trackViewOrder = (order, reduxState, currencySymbol) => {
  if (!amplitudeClientReady || !order?.code) {
    return;
  }
  try {
    if (!Array.isArray(order.items) || order.items.length === 0) {
      return;
    }
    try {
      identifyInternalUserIdForViewOrder(order, reduxState);
    } catch {
      /* identify must not block View Order */
    }
    const sym =
      currencySymbol ||
      order?.store?.currency?.symbol ||
      reduxState?.user?.selectedStore?.currency?.symbol ||
      '$';
    const base = resolveViewCheckoutOrderBaseProperties(order, reduxState);
    const payload = {
      ...base,
      'Number of Items': sumOrderLineItemQuantities(order),
      Subtotal: formatOrderSubtotalForAmplitude(order, sym),
    };
    amplitude.track('View Order', payload);
  } catch (err) {
    try {
      const message = err instanceof Error ? err.message : String(err ?? 'unknown');
      ddLog.logError(new Error(`Amplitude trackViewOrder failed: ${message}`));
    } catch {
      /* logging must not affect view-order */
    }
  }
};

/**
 * View PDP (product display page).
 * @param {{ product: object }} params
 */
const trackViewPdp = ({ product } = {}) => {
  if (!amplitudeClientReady || !product?.id) {
    return;
  }
  const category = getProductRollupCategory(product);
  const rawName = product.title ?? product.metaHeading;
  const name =
    rawName != null && String(rawName).trim() !== ''
      ? String(rawName).trim()
      : undefined;
  const id = String(product.id);
  if (!name) {
    return;
  }
  const flags = {
    isBox: Boolean(product.hasAssortments),
    isCustom: Boolean(product.options?.length),
  };
  const payload = {
    'Product Category': category ?? '',
    'Product Name': name,
    'Product ID': id,
    'Product Type': getAmplitudeProductType(product, flags),
  };
  amplitude.track('View PDP', payload);
};

/**
 * Add to Order event after successful add.
 * @param {{
 *   pathname?: string,
 *   product: object,
 *   quantity: number,
 *   subtotalDisplay: string,
 *   isGift: boolean,
 *   optionCount?: number,
 *   isBox?: boolean,
 *   isCustom?: boolean,
 * }} params
 */
const trackAddToOrder = ({
  pathname,
  product,
  quantity,
  subtotalDisplay,
  isGift,
  optionCount,
  isBox,
  isCustom,
} = {}) => {
  if (!amplitudeClientReady || !product) {
    return;
  }
  const pageName = resolveAddToOrderPageName(pathname);
  if (pageName === 'Home') {
    return;
  }
  const category = getProductRollupCategory(product);
  const rawName = product.title ?? product.metaHeading;
  const prodName =
    rawName != null && String(rawName).trim() !== ''
      ? String(rawName).trim()
      : undefined;
  const id = product.id != null ? String(product.id) : undefined;
  if (!id || !prodName) {
    return;
  }
  const qty = Number(quantity);
  const safeQty = Number.isFinite(qty) && qty > 0 ? qty : 1;
  const opts =
    optionCount != null && Number.isFinite(Number(optionCount))
      ? Math.max(0, Math.floor(Number(optionCount)))
      : 0;

  amplitude.track('Add to Order', {
    'Page Name': pageName,
    'Product Category': category ?? '',
    'Product Name': prodName,
    'Product ID': id,
    'Product Type': getAmplitudeProductType(product, { isBox, isCustom }),
    'Is A Gift': Boolean(isGift),
    Quantity: safeQty,
    Subtotal: subtotalDisplay != null ? String(subtotalDisplay) : '',
    'Number of Product Options': opts,
  });
};

const extractProductIdFromAddPayload = (addPayload) => {
  if (!addPayload || typeof addPayload !== 'object') {
    return undefined;
  }
  if (addPayload.product != null) {
    return addPayload.product;
  }
  if (addPayload.productId != null) {
    return addPayload.productId;
  }
  return undefined;
};

const resolveLineForAddToOrderAnalytics = ({
  order,
  previousOrder,
  productId,
  orderProductId,
  orderLine,
}) => {
  if (orderLine) {
    return orderLine;
  }
  if (order && orderProductId != null) {
    const byId = order.items?.find((li) => String(li?.id) === String(orderProductId));
    if (byId) {
      return byId;
    }
  }
  if (previousOrder && order && productId != null) {
    const added = findAddedOrderLine(previousOrder, order, productId);
    if (added) {
      return added;
    }
  }
  if (order && productId != null) {
    return findLatestOrderLineForProduct(order, productId);
  }
  return undefined;
};

/**
 * For add-to-cart flows with an order snapshot (tests / advanced callers).
 */
const trackAddToOrderFromOrderResult = ({
  pathname,
  product,
  quantity,
  isGift,
  isBox,
  isCustom,
  order,
  previousOrder,
  orderLine,
  orderProductId,
  currencySymbol = '$',
}) => {
  if (!product?.id) {
    return;
  }
  const line = resolveLineForAddToOrderAnalytics({
    order,
    previousOrder,
    productId: product.id,
    orderProductId,
    orderLine,
  });
  const optionCount = line != null ? countLineItemProductOptions(line) : 0;
  const safeQty = (() => {
    const q = Number(line?.quantity ?? quantity);
    return Number.isFinite(q) && q > 0 ? q : 1;
  })();
  let subtotalDisplay;
  if (line != null) {
    const unit = Number(line.price);
    if (Number.isFinite(unit)) {
      subtotalDisplay = `${currencySymbol}${(unit * safeQty).toFixed(2)}`;
    }
  }
  if (subtotalDisplay == null) {
    const unit = Number(product.price);
    const subtotalNum = Number.isFinite(unit) ? unit * safeQty : 0;
    subtotalDisplay = `${currencySymbol}${subtotalNum.toFixed(2)}`;
  }
  const resolvedIsGift = Boolean(line?.isGift ?? isGift);
  const resolvedIsBox =
    isBox ??
    Boolean(line?.product?.hasAssortments || (line?.boxOptions?.length > 0));
  const resolvedIsCustom =
    isCustom ??
    Boolean(
      (line?.productOptions?.length > 0) || (product?.options?.length > 0),
    );
  trackAddToOrder({
    pathname,
    product,
    quantity: safeQty,
    subtotalDisplay,
    isGift: resolvedIsGift,
    optionCount,
    isBox: resolvedIsBox,
    isCustom: resolvedIsCustom,
  });
};

/**
 * Fires after successful add-product mutations from the GraphQL order repository (not createCart).
 * @param {{ previousOrder: object, order: object, addPayload: object, analytics?: object, reduxState: object }} params
 */
const afterSuccessfulAddProduct = ({
  previousOrder,
  order,
  addPayload,
  analytics = {},
  reduxState,
}) => {
  if (!amplitudeClientReady || !order || !reduxState) {
    return;
  }
  const productId = extractProductIdFromAddPayload(addPayload);
  if (productId == null) {
    return;
  }

  const prevLen = Array.isArray(previousOrder?.items) ? previousOrder.items.length : 0;
  const nextLen = Array.isArray(order?.items) ? order.items.length : 0;
  if (prevLen === 0 && nextLen > 0) {
    try {
      const orderTime =
        order.createdAt != null ? new Date(order.createdAt) : new Date();
      const payload = resolveAddFirstItemToCartEventPropertiesFromOrder(
        order,
        reduxState,
        orderTime,
      );
      if (payload?.['Store ID'] != null && String(payload['Store ID']).trim() !== '') {
        amplitude.track('Add First Item to Cart', payload);
      }
    } catch {
      /* analytics must not affect cart */
    }
  }

  try {
    const currencySymbol =
      analytics.currencySymbol ||
      order?.store?.currency?.symbol ||
      reduxState?.currency?.currency?.symbol ||
      '$';

    const line = resolveLineForAddToOrderAnalytics({
      order,
      previousOrder,
      productId,
      orderProductId: analytics.orderProductId,
      orderLine: analytics.orderLine,
    });
    const lineProduct = line?.product;
    const ep = analytics.enrichmentProduct;
    const productForTrack = {
      ...(ep || {}),
      ...(lineProduct || {}),
      id: lineProduct?.id ?? productId ?? ep?.id,
      price:
        line?.price != null && line.price !== ''
          ? line.price
          : lineProduct?.price ?? ep?.price,
    };
    if (productForTrack.id == null) {
      return;
    }

    trackAddToOrderFromOrderResult({
      pathname: analytics.pathname,
      product: productForTrack,
      quantity: addPayload?.quantity,
      isGift: addPayload?.isGift,
      isBox: analytics.isBox,
      isCustom: analytics.isCustom,
      order,
      previousOrder,
      orderLine: line,
      orderProductId: analytics.orderProductId,
      currencySymbol,
    });
  } catch {
    /* analytics must not affect cart */
  }
};

/**
 * Amplitude — Place Order (enabled checkout button).
 * @param {object} order
 * @param {object} [reduxState]
 */
const trackPlaceOrder = (order, reduxState) => {
  if (!amplitudeClientReady || !order) {
    return;
  }
  try {
    const payload = resolvePlaceOrderEventProperties(order, reduxState);
    if (!payload) {
      return;
    }
    amplitude.track('Place Order', payload);
  } catch {
    /* analytics must not affect checkout */
  }
};

/**
 * Amplitude — Complete Order (success → Order Success screen).
 * @param {object} order
 * @param {object} [reduxState]
 * @param {object} [meta] — forwarded to {@link resolveCompleteOrderEventProperties}
 */
const trackCompleteOrder = (order, reduxState, meta) => {
  if (!amplitudeClientReady || !order) {
    return;
  }
  try {
    const payload = resolveCompleteOrderEventProperties(order, reduxState, meta);
    if (!payload) {
      return;
    }
    amplitude.track('Complete Order', payload);
  } catch {
    /* analytics must not affect checkout */
  }
};

export default {
  initialize,
  getReadyPromise,
  syncUserId,
  trackViewPage,
  trackClickedPromotionElement,
  trackClickPageContent,
  trackCmsMappedPageClick,
  trackFilterPdp,
  trackViewGiftOptions,
  trackSelectGiftOption,
  trackAddSuggestedItem,
  trackSearchMenu,
  trackClickProductCard,
  trackEnterPromoCodeGiftCard,
  trackSetDropoffLocation,
  trackAddCreditCard,
  trackClickToModifyItem,
  trackUpdateItem,
  trackRemoveItem,
  trackModifyItemQuantity,
  trackSignIn,
  trackCreateAccount,
  trackResetPassword,
  trackViewCheckout,
  trackViewOrder,
  trackClickNavPrimary,
  trackOpenHamburgerMenu,
  trackClickNavSecondary,
  trackEnterAddress,
  trackViewDeliveryPickupWidget,
  trackViewPdp,
  trackAddToOrder,
  trackAddToOrderFromOrderResult,
  afterSuccessfulAddProduct,
  trackStartOrder,
  trackPlaceOrder,
  trackCompleteOrder,
  resolveAmplitudeServerUrl,
  isAmplitudeProxyEnabled,
  isUnsubstitutedEnvToken,
};
